/** @format */

import React from 'react';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../firebase/config';
import { collection, query, where, onSnapshot, doc, addDoc, setDoc, serverTimestamp, orderBy } from 'firebase/firestore';
import { useUsers } from '../hooks/useUsers';

interface DMThread {
  id: string;
  participants: string[];
  participantNames?: Record<string, string>;
  lastMessagePreview?: string;
  lastMessageFrom?: string;
  lastMessageAt?: any;
  createdAt?: any;
  updatedAt?: any;
  unreadBy?: Record<string, number>;
}

interface DMMessage {
  id?: string;
  senderId: string;
  receiverId: string;
  content: string;
  createdAt: any;
  readBy: string[];
}

const Inbox: React.FC = () => {
  const { currentUser } = useAuth();
  const [threads, setThreads] = React.useState<DMThread[]>([]);
  const [selectedThreadId, setSelectedThreadId] = React.useState<string | null>(null);
  const [messages, setMessages] = React.useState<DMMessage[]>([]);
  const [composer, setComposer] = React.useState('');
  const [sending, setSending] = React.useState(false);
  const { fetchUsers, getUserDisplayName, getUserAvatar } = useUsers();
  const messagesEndRef = React.useRef<HTMLDivElement | null>(null);

  // Subscribe to user's threads
  React.useEffect(() => {
    if (!currentUser) return;
    const q = query(collection(db, 'dm_threads'), where('participants', 'array-contains', currentUser.uid));
    const unsub = onSnapshot(q, (snap) => {
      const list: DMThread[] = [];
      snap.forEach((d) => list.push({ id: d.id, ...(d.data() as any) }));
      // Sort client-side by lastMessageAt desc if available
      list.sort((a, b) => {
        const av = (a.lastMessageAt && a.lastMessageAt.toMillis) ? a.lastMessageAt.toMillis() : 0;
        const bv = (b.lastMessageAt && b.lastMessageAt.toMillis) ? b.lastMessageAt.toMillis() : 0;
        return bv - av;
      });
      setThreads(list);
      // Pre-fetch participant profiles for avatars/names
      const ids = new Set<string>();
      list.forEach(t => t.participants.forEach(p => ids.add(p)));
      if (ids.size > 0) fetchUsers(Array.from(ids));
      if (!selectedThreadId && list.length > 0) {
        setSelectedThreadId(list[0].id);
      }
    });
    return () => unsub();
  }, [currentUser, selectedThreadId, fetchUsers]);

  // Subscribe to messages for selected thread
  React.useEffect(() => {
    if (!selectedThreadId) {
      setMessages([]);
      return;
    }
    const q = query(collection(db, 'dm_threads', selectedThreadId, 'messages'), orderBy('createdAt'));
    const unsub = onSnapshot(q, (snap) => {
      const list: DMMessage[] = [];
      snap.forEach((d) => list.push({ id: d.id, ...(d.data() as any) }));
      setMessages(list);
    });
    return () => unsub();
  }, [selectedThreadId]);

  const selectedThread = React.useMemo(() => threads.find(t => t.id === selectedThreadId) || null, [threads, selectedThreadId]);
  const otherUserId = React.useMemo(() => {
    if (!currentUser || !selectedThread) return null;
    const [a, b] = selectedThread.participants;
    return a === currentUser.uid ? b : a;
  }, [currentUser, selectedThread]);

  const handleSend = async () => {
    if (!currentUser || !selectedThread || !otherUserId) return;
    const text = composer.trim();
    if (!text) return;
    try {
      setSending(true);
      const nowTs = serverTimestamp();
      await addDoc(collection(db, 'dm_threads', selectedThread.id, 'messages'), {
        senderId: currentUser.uid,
        receiverId: otherUserId,
        content: text,
        createdAt: nowTs,
        readBy: [currentUser.uid]
      });
      await setDoc(doc(db, 'dm_threads', selectedThread.id), {
        lastMessagePreview: text.slice(0, 120),
        lastMessageFrom: currentUser.uid,
        lastMessageAt: nowTs,
        updatedAt: nowTs
      }, { merge: true });
      setComposer('');
    } catch (e) {
      // Optionally attach a toast here
      console.error('Failed to send message', e);
    } finally {
      setSending(false);
    }
  };

  // Auto scroll to bottom on new messages
  React.useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  const handleComposerKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (!currentUser) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 text-center">
            <h1 className="text-xl font-semibold mb-2">Inbox</h1>
            <p className="text-gray-600">Please sign in to view your messages.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-8">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="grid grid-cols-1 md:grid-cols-3">
            {/* Threads list */}
            <div className="border-r border-gray-200 md:col-span-1 bg-gray-50">
              <div className="p-4 border-b border-gray-200 bg-white">
                <h2 className="text-lg font-semibold text-gray-900">Inbox</h2>
                <p className="text-xs text-gray-500">Direct Messages</p>
              </div>
              <div className="max-h-[70vh] overflow-y-auto">
                {threads.length === 0 ? (
                  <div className="p-4 text-sm text-gray-500">No conversations yet.</div>
                ) : (
                  threads.map((t) => {
                    const otherId = currentUser && t.participants.find(p => p !== currentUser.uid);
                    const name = otherId ? (getUserDisplayName(otherId) || (t.participantNames && t.participantNames[otherId]) || 'Unknown User') : 'Unknown User';
                    const avatar = otherId ? getUserAvatar(otherId) : null;
                    const isActive = selectedThreadId === t.id;
                    return (
                      <button
                        key={t.id}
                        onClick={() => setSelectedThreadId(t.id)}
                        className={`w-full text-left p-4 border-b border-gray-100 hover:bg-gray-100 transition-colors ${isActive ? 'bg-gray-100' : 'bg-white'}`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center min-w-0">
                            {avatar ? (
                              <img src={avatar} alt={name} className="w-9 h-9 rounded-full object-cover border border-gray-200 mr-3" />
                            ) : (
                              <div className="w-9 h-9 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center text-sm font-semibold mr-3 border border-gray-200">
                                {name.charAt(0).toUpperCase()}
                              </div>
                            )}
                            <div className="min-w-0">
                              <div className="font-medium text-gray-900 truncate">{name}</div>
                              <div className="text-xs text-gray-500 truncate max-w-[180px]">{t.lastMessagePreview || 'New conversation'}</div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 ml-2">
                            {t.unreadBy && currentUser && (t.unreadBy[currentUser.uid] || 0) > 0 && (
                              <span className="inline-flex items-center justify-center min-w-[18px] h-5 px-1 rounded-full bg-primary-600 text-white text-[10px]">
                                {t.unreadBy[currentUser.uid]}
                              </span>
                            )}
                            {t.lastMessageAt && t.lastMessageAt.toDate && (
                              <div className="text-xs text-gray-500 whitespace-nowrap">
                                {t.lastMessageAt.toDate().toLocaleDateString()}
                              </div>
                            )}
                          </div>
                        </div>
                      </button>
                    );
                  })
                )}
              </div>
            </div>

            {/* Messages panel */}
            <div className="md:col-span-2 flex flex-col min-h-[70vh]">
              <div className="p-4 border-b border-gray-100">
                <h3 className="text-lg font-semibold text-gray-900">Conversation</h3>
              </div>
              <div className="flex-1 p-4 overflow-y-auto space-y-3 bg-gray-50">
                {(!selectedThread || messages.length === 0) ? (
                  <div className="text-sm text-gray-500">{selectedThread ? 'No messages yet.' : 'Select a conversation.'}</div>
                ) : (
                  messages.map((m) => {
                    const isMe = m.senderId === currentUser.uid;
                    return (
                      <div key={m.id} className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
                        <div className={`flex items-end max-w-[80%] ${isMe ? 'flex-row-reverse' : ''}`}>
                          {!isMe && otherUserId && (
                            getUserAvatar(otherUserId) ? (
                              <img src={getUserAvatar(otherUserId)!} alt={getUserDisplayName(otherUserId)} className="w-7 h-7 rounded-full object-cover border border-gray-200 mx-2" />
                            ) : (
                              <div className="w-7 h-7 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center text-[10px] font-semibold mx-2 border border-gray-200">
                                {otherUserId ? getUserDisplayName(otherUserId).charAt(0).toUpperCase() : '?'}
                              </div>
                            )
                          )}
                          <div className={`rounded-2xl px-3 py-2 text-sm shadow-sm ${isMe ? 'bg-primary-600 text-white rounded-br-none' : 'bg-white border border-gray-200 text-gray-900 rounded-bl-none'}`}>
                            <div className="whitespace-pre-wrap break-words">{m.content}</div>
                            {('createdAt' in m && (m as any).createdAt && (m as any).createdAt.toDate) && (
                              <div className={`text-[10px] mt-1 ${isMe ? 'text-white/80' : 'text-gray-500'}`}>
                                {(m as any).createdAt.toDate().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
                <div ref={messagesEndRef} />
              </div>
              {/* Composer */}
              <div className="p-4 border-t border-gray-200 flex items-center gap-3 bg-white">
                <textarea
                  value={composer}
                  onChange={(e) => setComposer(e.target.value)}
                  onKeyDown={handleComposerKeyDown}
                  placeholder="Write a message..."
                  className="flex-1 border border-gray-300 rounded-lg p-2 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 min-h-[42px]"
                  maxLength={1000}
                />
                <button
                  onClick={handleSend}
                  disabled={!selectedThread || !otherUserId || sending || composer.trim().length === 0}
                  className={`px-4 py-2 rounded-lg text-white text-sm font-medium ${(!selectedThread || !otherUserId || sending || composer.trim().length === 0) ? 'bg-gray-400 cursor-not-allowed' : 'bg-primary-600 hover:bg-primary-700'} transition-colors`}
                >
                  {sending ? 'Sending...' : 'Send'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Inbox;


